import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSiteSettings } from './SiteSettingsContext';
import { isExternalHeroActionPath, normalizeHeroActionPath } from './storefrontHeroActions';

function isAnnouncementEnabled(settings) {
  const v = settings?.announcement_enabled;
  return v === true || v === 1 || v === '1';
}

function buildAnnouncementSlides(settings) {
  const raw = Array.isArray(settings?.announcement_slides) ? settings.announcement_slides : [];
  const fromSlides = raw
    .map((slide) => ({
      text: String(slide?.text || '').trim(),
      link: String(slide?.link || '').trim(),
    }))
    .filter((slide) => slide.text);
  if (fromSlides.length) return fromSlides;

  const text = String(settings?.announcement_text || '').trim();
  if (!text) return [];
  return [
    {
      text,
      link: String(settings?.announcement_link || '').trim(),
    },
  ];
}

function AnnouncementSlideMessage({ slide }) {
  const text = String(slide?.text || '').trim();
  const link = normalizeHeroActionPath(slide?.link);
  if (!text) return null;

  if (link && !isExternalHeroActionPath(link)) {
    return (
      <Link to={link} className="site-announcement-bar__message">
        {text}
      </Link>
    );
  }
  if (link) {
    return (
      <a href={link} className="site-announcement-bar__message" target="_blank" rel="noopener noreferrer">
        {text}
      </a>
    );
  }
  return <span className="site-announcement-bar__message">{text}</span>;
}

/**
 * Shopify-style utility strip — full width, above the main header (site-wide).
 * Rotates when multiple live campaigns (or site + campaigns) provide messages.
 */
export default function HomeAnnouncementBar() {
  const { settings } = useSiteSettings();
  const { pathname } = useLocation();
  const slides = useMemo(() => buildAnnouncementSlides(settings), [settings]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    setIndex(0);
    setVisible(true);
  }, [slides]);

  useEffect(() => {
    if (slides.length <= 1) return undefined;

    const seconds = Math.max(4, Number(settings?.announcement_rotate_seconds) || 6);
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    if (reduced) return undefined;

    const fadeMs = 280;
    const id = window.setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % slides.length);
        setVisible(true);
      }, fadeMs);
    }, seconds * 1000);

    return () => window.clearInterval(id);
  }, [slides, settings?.announcement_rotate_seconds]);

  if (pathname.startsWith('/admin')) return null;
  if (!isAnnouncementEnabled(settings)) return null;
  if (!slides.length) return null;

  const slide = slides[index] || slides[0];
  const rotating = slides.length > 1;

  return (
    <div
      className={`site-announcement-bar${rotating ? ' site-announcement-bar--rotating' : ''}`}
      role="region"
      aria-label="Store announcements"
      aria-live={rotating ? 'polite' : undefined}
    >
      <div className="site-announcement-bar__inner">
        <div
          className={`site-announcement-bar__track${visible ? ' is-visible' : ''}`}
          key={`${index}-${slide.text}`}
        >
          <AnnouncementSlideMessage slide={slide} />
        </div>
        {rotating ? (
          <div className="site-announcement-bar__dots" aria-hidden="true">
            {slides.map((item, i) => (
              <span
                key={`${item.text}-${i}`}
                className={`site-announcement-bar__dot${i === index ? ' is-active' : ''}`}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
